import { Link, useParams } from "react-router-dom";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { getTerm, categoryLabel, DISCLAIMER_TEXT } from "@/data/glossary";
import { PublicShell, SignupCta, useSeo, LinkedText } from "./PublicShell";

export default function GlossaryTerm() {
  const { slug } = useParams();
  const term = getTerm(slug);

  useSeo({
    title: term ? `${term.title} | Canadian Autism & Neurodiversity Glossary | KidPath` : "Term not found | KidPath",
    description: term ? `Learn what ${term.title} means, why it matters for Canadian families, and how it may relate to autism supports, school planning, therapy, or funding.` : undefined,
    noindex: !term,
    jsonLd: term ? {
      "@context": "https://schema.org", "@type": "DefinedTerm",
      name: term.title,
      alternateName: term.aka && term.aka.length ? term.aka : undefined,
      description: term.definition,
      inDefinedTermSet: { "@type": "DefinedTermSet", name: "KidPath Canadian Autism & Neurodiversity Glossary", url: "https://kidpath.ca/terms/" },
    } : undefined,
  });

  if (!term) {
    return (
      <PublicShell>
        <div className="max-w-[760px] mx-auto px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-extrabold mb-3">Term not found</h1>
          <p className="text-kid-muted mb-6">We couldn't find that glossary term.</p>
          <Link to="/terms" className="inline-flex items-center gap-1.5 rounded-full bg-kid-tealcta text-white font-semibold px-5 py-2.5" data-testid="term-back-to-glossary">Browse the glossary</Link>
        </div>
      </PublicShell>
    );
  }

  const related = (term.related || []).map(getTerm).filter(Boolean);

  return (
    <PublicShell>
      <article className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1.5 text-sm text-kid-muted mb-6 flex-wrap" data-testid="term-breadcrumbs">
          <Link to="/terms" className="hover:text-kid-teal">Glossary</Link>
          <ChevronRight size={15} />
          <Link to={`/terms/category/${term.category}`} className="hover:text-kid-teal">{categoryLabel(term.category)}</Link>
          <ChevronRight size={15} />
          <span className="text-kid-navy font-medium">{term.title}</span>
        </nav>

        <Link to={`/terms/category/${term.category}`} className="inline-block text-xs font-semibold uppercase tracking-widest text-kid-tealcta mb-3 hover:underline" data-testid="term-category-link">{categoryLabel(term.category)}</Link>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight" data-testid="term-title">
          {term.title}{term.acronym ? <span className="text-kid-muted"> ({term.acronym})</span> : null}
        </h1>
        {term.aka && term.aka.length > 0 && (
          <p className="text-kid-muted mt-3" data-testid="term-aka"><span className="font-semibold text-kid-navy">Also known as:</span> {term.aka.join(", ")}</p>
        )}

        <div className="prose-kid mt-8 space-y-8">
          <section>
            <h2 className="font-display text-2xl font-extrabold mb-2">What does {term.title} mean?</h2>
            <p className="text-kid-navy/90 leading-relaxed" data-testid="term-definition"><LinkedText text={term.definition} currentSlug={term.slug} /></p>
          </section>

          {term.whyMatters && (
            <section>
              <h2 className="font-display text-2xl font-extrabold mb-2">Why this term matters</h2>
              <p className="text-kid-navy/90 leading-relaxed"><LinkedText text={term.whyMatters} currentSlug={term.slug} /></p>
            </section>
          )}

          {term.canadianContext && (
            <section>
              <h2 className="font-display text-2xl font-extrabold mb-2">Canadian context</h2>
              <p className="text-kid-navy/90 leading-relaxed"><LinkedText text={term.canadianContext} currentSlug={term.slug} /></p>
            </section>
          )}
        </div>

        {/* Mid-page CTA #1 */}
        <div className="my-9"><SignupCta variant="inline" testid="term-cta-inline" /></div>

        {related.length > 0 && (
          <section className="mb-10">
            <h2 className="font-display text-2xl font-extrabold mb-4">Related terms</h2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="term-related">
              {related.map((r) => (
                <Link key={r.slug} to={`/terms/${r.slug}`} data-testid={`term-related-${r.slug}`}
                  className="kid-card kid-card-interactive p-4 flex items-center justify-between gap-3">
                  <span className="font-semibold text-kid-navy">{r.title}{r.acronym ? ` (${r.acronym})` : ""}</span>
                  <ChevronRight size={18} className="text-kid-muted shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="rounded-2xl bg-kid-cream/70 border border-kid-line p-5 text-sm text-kid-muted mb-10" data-testid="term-disclaimer">
          {DISCLAIMER_TEXT}
        </div>

        {/* CTA #2 */}
        <SignupCta variant="band" testid="term-cta-band" />

        <div className="mt-8">
          <Link to="/terms" className="inline-flex items-center gap-1.5 text-kid-teal font-semibold" data-testid="term-back-link"><ArrowLeft size={16} /> Back to glossary</Link>
        </div>
      </article>
    </PublicShell>
  );
}
